import {
    HasKids,
    type KidInfo,
    type MembershipProfileAnswerUpsertDto,
    type MembershipProfileAnswerWithQuestion,
    type MembershipProfileKidAnswer,
    type ProfileAnswer,
    QuestionKey,
} from '@fruitful/member-profile';
import { useAtomValue } from 'jotai';
import { Column, type ColumnEditorOptions } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
} from 'primereact/datatable';
import { useCallback, useEffect, useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H6Emphasized,
    IconButton,
    Spinner,
    TrashIcon,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import {
    DropdownEditor,
    TextEditor,
} from '../../common/components/GridCellEditors';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../entities/entityAtoms';
import { useRefetchMembershipDetailsMutationOptions } from '../../entities/membership/hooks/useRefetchMembershipDetailsMutationOptions';
import useUpsertMembershipProfileAnswer from '../hooks/useUpsertMembershipProfileAnswer';
import { findMembershipProfileAnswerByQuestionKey } from '../utils/memberProfileUtils';
import { AddKidModal } from './AddKidModal';

interface KidsInfoProps {
    membershipProfileAnswers: Array<
        MembershipProfileAnswerWithQuestion<ProfileAnswer>
    >;
}

export const MembershipKidsTable = ({
    membershipProfileAnswers,
}: KidsInfoProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [kidsData, setKidsData] = useState<KidInfo[]>([]);
    const [hasKidsAnswer, setHasKidsAnswer] = useState<HasKids | null>(null);
    const [isAddKidModalOpen, setIsAddKidModalOpen] = useState(false);

    const toast = useToastController();
    const adminUser = useAtomValue(adminUserAtom);
    const membership = useAtomValue(selectedMembershipAtom);
    const canEditMember =
        adminUser.authorization.selectedMembership.general.canEdit;

    const data = findMembershipProfileAnswerByQuestionKey<
        ProfileAnswer,
        MembershipProfileKidAnswer
    >(membershipProfileAnswers, QuestionKey.KIDS);

    useEffect(() => {
        if (data?.answer) {
            setHasKidsAnswer(data.answer.answer);
            setKidsData(data.answer.additionalInformation ?? []);
        }
    }, [data]);

    const kidsQuestionAndAnswerData = [
        { question: 'Do you have kids?', answer: hasKidsAnswer },
    ];

    // Setup upsert hook
    const refetchMembershipMutationOptions =
        useRefetchMembershipDetailsMutationOptions(membership);
    const { mutateAsync: upsertMembershipProfileAnswer } =
        useUpsertMembershipProfileAnswer(refetchMembershipMutationOptions);

    // Shared upsert function to dedupe common logic
    const performUpsert = useCallback(
        async (kidsDataToSave: MembershipProfileKidAnswer) => {
            if (!membership) return;

            setIsSubmitting(true);
            try {
                const upsertDto: MembershipProfileAnswerUpsertDto<MembershipProfileKidAnswer> =
                    {
                        membershipId: membership.id,
                        answeredByUserId: membership.users[0]?.id as string, // Default to primary member as the answering user.
                        questionKey: QuestionKey.KIDS,
                        answer: kidsDataToSave,
                    };

                await upsertMembershipProfileAnswer({
                    body: [upsertDto],
                    urlParams: {
                        membershipId: membership.id,
                    },
                });

                toast.show(
                    'Kids data updated successfully!',
                    MutationSuccessToastOptions,
                );
            } catch (error) {
                toast.show(
                    'Error updating kids data',
                    MutationErrorToastOptions(error),
                );
            } finally {
                setIsSubmitting(false);
            }
        },
        [membership, upsertMembershipProfileAnswer, toast],
    );

    const onQuestionRowEditComplete = useCallback(
        async ({ newData }: DataTableRowEditCompleteEvent) => {
            const answer = newData as { question: string; answer: HasKids };

            if (!membership || answer.answer == null) return;

            const kidsDataToSave: MembershipProfileKidAnswer = {
                answer: answer.answer,
                additionalInformation:
                    answer.answer === HasKids.YES
                        ? kidsData.filter((kid) => kid.birthYear.trim() !== '')
                        : undefined,
            };

            await performUpsert(kidsDataToSave);
        },
        [membership, kidsData, performUpsert],
    );

    const onKidsRowEditComplete = useCallback(
        async ({ newData, index }: DataTableRowEditCompleteEvent) => {
            if (!membership || hasKidsAnswer == null) return;

            const updatedKids = kidsData.map((kid, i) =>
                index === i ? newData : kid,
            ) as KidInfo[];

            const kidsDataToSave: MembershipProfileKidAnswer = {
                answer: hasKidsAnswer,
                additionalInformation:
                    hasKidsAnswer === HasKids.YES
                        ? updatedKids.filter(
                              (kid) => kid.birthYear.trim() !== '',
                          )
                        : undefined,
            };

            await performUpsert(kidsDataToSave);
        },
        [membership, hasKidsAnswer, kidsData, performUpsert],
    );

    const onRemoveKid = useCallback(
        async (index: number) => {
            if (!membership || hasKidsAnswer == null) return;

            const updatedKids = kidsData.filter((_, i) => i !== index);

            const kidsDataToSave: MembershipProfileKidAnswer = {
                answer: hasKidsAnswer,
                additionalInformation:
                    hasKidsAnswer === HasKids.YES
                        ? updatedKids.filter(
                              (kid) => kid.birthYear.trim() !== '',
                          )
                        : undefined,
            };

            await performUpsert(kidsDataToSave);
        },
        [membership, hasKidsAnswer, kidsData, performUpsert],
    );

    const handleModalClose = useCallback(() => {
        setIsAddKidModalOpen(false);
    }, []);

    const handleAddKidSubmit = useCallback(
        async (newKid: KidInfo) => {
            if (!membership || hasKidsAnswer == null) return;

            const updatedKids = [...kidsData, newKid];

            const kidsDataToSave: MembershipProfileKidAnswer = {
                answer: hasKidsAnswer,
                additionalInformation:
                    hasKidsAnswer === HasKids.YES
                        ? updatedKids.filter(
                              (kid) => kid.birthYear.trim() !== '',
                          )
                        : undefined,
            };

            await performUpsert(kidsDataToSave);
            setIsAddKidModalOpen(false);
        },
        [membership, hasKidsAnswer, kidsData, performUpsert],
    );

    const Header = (
        <XStack justifyContent="space-between" alignItems="center">
            <H6Emphasized>Kids details</H6Emphasized>
        </XStack>
    );

    const calculateAge = (birthYear: string) => {
        const currentYear = new Date().getFullYear();
        return currentYear - parseInt(birthYear, 10);
    };

    const BirthYearRenderer = (rowData: KidInfo) => {
        const age = calculateAge(rowData.birthYear);
        return `${rowData.birthYear} (~Age: ${age})`;
    };

    const HasKidsEditor = (options: ColumnEditorOptions) => {
        const hasKids = Object.values(HasKids);

        return (
            <DropdownEditor
                options={options}
                dropdownProps={{
                    options: hasKids,
                }}
            />
        );
    };

    const DeleteKidRenderer = (
        _: KidInfo,
        { rowIndex }: { rowIndex: number },
    ) => {
        return (
            <IconButton
                variant="tertiary"
                tone="danger"
                icon={TrashIcon}
                onPress={async () => {
                    await onRemoveKid(rowIndex);
                }}
                disabled={isSubmitting}
            />
        );
    };

    const KidsQuestionTable = (
        <DataTable
            value={kidsQuestionAndAnswerData}
            size="small"
            tableStyle={{ width: '100%', fontSize: 13 }}
            scrollable
            editMode="row"
            onRowEditComplete={onQuestionRowEditComplete}
        >
            <Column field="question" header="Question" />
            <Column
                field="answer"
                header="Answer"
                editor={(options) => HasKidsEditor(options)}
            />
            <Column rowEditor />
        </DataTable>
    );

    const KidsTable = (
        <DataTable
            value={kidsData}
            size="small"
            tableStyle={{ width: '100%', fontSize: 13 }}
            scrollable
            editMode="row"
            onRowEditComplete={onKidsRowEditComplete}
        >
            <Column
                field="firstName"
                header="First Name"
                editor={(options) => TextEditor({ options })}
            />
            <Column
                field="birthYear"
                header="Birth Year / Age"
                body={BirthYearRenderer}
                editor={(options) => TextEditor({ options })}
            />
            <Column rowEditor hidden={!canEditMember} />
            <Column
                header=""
                body={DeleteKidRenderer}
                style={{ width: '80px' }}
                hidden={!canEditMember}
            />
        </DataTable>
    );

    return (
        <YStack gap={16}>
            {Header}
            {isSubmitting && <Spinner />}
            <YStack gap={16}>
                {KidsQuestionTable}

                {hasKidsAnswer === HasKids.YES && (
                    <YStack gap={12}>
                        <XStack
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <H6Emphasized>Kids Information</H6Emphasized>
                            {canEditMember && (
                                <Button
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.SMALL}
                                    text="Add Kid"
                                    width="auto"
                                    onPress={() => {
                                        setIsAddKidModalOpen(true);
                                    }}
                                    disabled={isSubmitting}
                                />
                            )}
                        </XStack>
                        {kidsData.length > 0 ? (
                            KidsTable
                        ) : (
                            <BodyMediumRegular>
                                No kids added yet.
                            </BodyMediumRegular>
                        )}
                    </YStack>
                )}
            </YStack>
            {canEditMember && (
                <AddKidModal
                    isOpen={isAddKidModalOpen}
                    onClose={handleModalClose}
                    onSubmit={handleAddKidSubmit}
                />
            )}
        </YStack>
    );
};
